import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Table, Empty } from "antd";
import moment from "moment";

import { renderExpandedRow } from "./ExpandedRow";
import { MatchButton } from "./styles";
import { Transaction } from "../../../../types";
import { formatAmountInCents } from "../../../../utils";
import TransactionSearch from "../TransactionSearch";
import SelectBadge from "../../TransactionView/SelectBadges";
import { DISPLAY_DATE_FORMAT } from "@/v1/constants";

const Wrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  position: relative;
  .ant-table-wrapper {
    width: 100%;
  }
  gap: 15px;
`;

const RightSideContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const BadgeContainer = styled.div`
  padding-bottom: 10px;
`;

const TransactionList = ({
  transactions,
  matchDocumentToTransaction,
  bookAndMatchTransaction,
  customerEmail,
  receiptId,
  prefillAmount,
}: {
  transactions: Transaction[];
  matchDocumentToTransaction: (transactionId: string) => void;
  bookAndMatchTransaction: (
    transaction: Transaction,
    vatCategoryCode: string,
    categoryCode: string
  ) => void;
  customerEmail?: string;
  receiptId?: string;
  prefillAmount?: number;
}) => {
  const [transactionsList, setTransactionsList] = useState(transactions);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(transactions.length);

  const columns = [
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      sorter: (a: Transaction, b: Transaction) => a.amount - b.amount,
      render: (amount: number, record: Transaction) =>
        formatAmountInCents(amount, true, undefined, record.currency),
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a: Transaction, b: Transaction) =>
        (a.name || "").localeCompare(b.name || ""),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      sorter: (a: Transaction, b: Transaction) =>
        (a.description || "").localeCompare(b.description || ""),
      render: (description: string) => description || "-",
    },
    {
      title: "Date",
      key: "date",
      dataIndex: "valutaDate",
      width: 120,
      sorter: (a: Transaction, b: Transaction) =>
        moment(a.valutaDate).isAfter(b.valutaDate) ? 1 : -1,
      render: (date: Date) => moment(date).format(DISPLAY_DATE_FORMAT),
    },
    {
      title: "",
      key: "match",
      render: (entry: Transaction) => (
        <RightSideContainer>
          {entry.assets?.length ? (
            <BadgeContainer>
              <SelectBadge info="Has asset" />
            </BadgeContainer>
          ) : null}
          <MatchButton
            data-test="matchedTransactionsList.matchButton"
            onClick={async (event) => {
              event.stopPropagation();
              await matchDocumentToTransaction(entry.id);
            }}
          >
            Match
          </MatchButton>
        </RightSideContainer>
      ),
    },
  ];

  const data = React.useMemo(() => transactionsList, [transactionsList]);

  const onTransactionsListUpdateHandler = (
    transactions: Transaction[],
    newTotal: number
  ) => {
    setTransactionsList(transactions);
    setTotal(newTotal);
  };

  useEffect(() => {
    setTransactionsList(transactions);
    setTotal(transactions.length);
    setPage(1);
  }, [transactions]);

  const onAssetsUpdate = (
    transactionId: string,
    assets: Transaction["assets"]
  ) => {
    setTransactionsList((current) =>
      current.map((transaction) =>
        transaction.id === transactionId
          ? { ...transaction, assets }
          : transaction
      )
    );
  };

  return (
    <Wrapper>
      <TransactionSearch
        onSearch={onTransactionsListUpdateHandler}
        customerEmail={customerEmail}
        receiptId={receiptId}
        prefillAmount={prefillAmount}
        page={page}
        pageSize={pageSize}
        onResetPage={() => setPage(1)}
      />
      <Table
        columns={columns}
        dataSource={data}
        bordered
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          onChange: (nextPage, nextPageSize) => {
            if (nextPageSize !== pageSize) {
              setPageSize(nextPageSize);
              setPage(1);
            } else {
              setPage(nextPage);
            }
          },
        }}
        expandable={{
          expandedRowRender: (transaction) => {
            return renderExpandedRow(
              transaction,
              bookAndMatchTransaction,
              onAssetsUpdate
            );
          },
          expandRowByClick: true,
        }}
        rowKey={(row) => row.id}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No transactions found"
            />
          ),
        }}
      />
    </Wrapper>
  );
};

export default TransactionList;
